/**
 * <tecton-video-panel> — mute toggle for an autoplaying brand video.
 *
 * Port of the toggle in BrandVideoPanel.tsx. Autoplay requires the video to
 * start muted; unmuting needs a user gesture, so the button both flips
 * `muted` and re-calls play().
 *
 * State lives in `data-muted` on the host, and the icons are swapped from that
 * attribute in CSS — so if the browser refuses to unmute, the icon stays
 * truthful rather than claiming audio is playing when it is not.
 */

class TectonVideoPanel extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.button = this.querySelector('[data-mute-toggle]');
    if (!this.video || !this.button) return;

    this.muteLabel = this.button.dataset.muteLabel || 'Mute video';
    this.unmuteLabel = this.button.getAttribute('aria-label') || 'Unmute video';

    this.dataset.muted = 'true';
    this.button.addEventListener('click', this.toggle);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.toggle);
  }

  toggle = async () => {
    const nextMuted = !this.video.muted;
    this.video.muted = nextMuted;

    if (!nextMuted) {
      try {
        await this.video.play();
      } catch {
        // Playback with audio was refused — restore muted so the icon and the
        // actual audio state do not diverge.
        this.video.muted = true;
      }
    }

    this.dataset.muted = String(this.video.muted);
    this.button.setAttribute('aria-label', this.video.muted ? this.unmuteLabel : this.muteLabel);
  };
}

if (!customElements.get('tecton-video-panel')) {
  customElements.define('tecton-video-panel', TectonVideoPanel);
}
